import { setRegisteredData } from "./registered-data";
import {
    startNinjaCut,
    teardownNinjaCut,
    pauseNinjaCut,
    resumeNinjaCut,
    preloadNinjaCutAssets,
} from "../../../games/ninja-cut/ninja-cut";

/**
 * In-modal navigation for the multi-step application flow (Ninja Cut).
 *
 * Screens live in #modal-form-application as .modal-screen[data-screen="..."] and are
 * shown one at a time. Navigation is driven by data attributes on buttons:
 *   - data-modal-next="<screen>"   → simply show that screen
 *   - data-modal-action="<action>" → run a behaviour (start/finish game, answer, decline)
 *
 * Flow: loading → confirmation → instr-1 → instr-2 → start → game → result → quest → thanks.
 * The trial (demo) game can be played UP TO THREE times: start → (start-demo) game → demo-result → game.
 */

const MODAL_SELECTOR = '#modal-form-application';
const MIN_LOADING_MS = 1500; // keep the loading screen up at least this long, even if assets cache instantly
const LOADING_AUTO_PROCEED = true; // TEMP: set true to re-enable auto-advance to confirmation (disabled for styling)
const MAX_DEMO_ATTEMPTS = 3;
// The game must be started within 2 h of submitting the entry (enforced server-side in
// NinjaCutController::start). Mirror that window client-side: auto-hide the stale modal after 2 h,
// unless the real game has already begun (the timer is cleared on game start).
const MODAL_AUTO_HIDE_MS = 2 * 60 * 60 * 1000;

let _data = null;
let _uuid = null;
let _score = null;
let _reason = null;
let _demoAttempts = 0;
let _autoHideTimer = null;

const $modal = () => $(MODAL_SELECTOR);
const csrf = () => $modal().data('game-csrf');
const endpoint = (action) => `/api/application/${_uuid}/${action}`;

function show(screen) {
    const $m = $modal();
    $m.find('.modal-screen').addClass('d-none');
    $m.find(`.modal-screen[data-screen="${screen}"]`).removeClass('d-none');
    $m.find('.modal-dev-screen').val(screen);
}

/** Entry point: called from the form submit-success handler. */
export function startApplicationModal(data) {
    _data = data;
    _uuid = data.uuid;
    _score = null;
    _reason = null;
    _demoAttempts = 0;

    // The submit handler suppresses ajax-form's post-success reset (see application.js), so do
    // it here: the native reset clears the fields AND fires the 'reset' event that mints a fresh
    // idempotency key (see form/idempotency.js), so a follow-up entry isn't rejected as a dupe.
    resetApplicationForm();

    $modal().modal('show');
    armAutoHide();
    runLoading();
}

/** Arm the 2 h auto-hide: once the start window passes, show the "start expired" screen on the
 *  (unstarted) modal — matching what the server would return if the user tried to start now. */
function armAutoHide() {
    clearAutoHide();
    _autoHideTimer = setTimeout(() => {
        _autoHideTimer = null;
        show('start-expired');
    }, MODAL_AUTO_HIDE_MS);
}

/** Cancel the auto-hide timer (e.g. once the real game has started, or the modal closed). */
function clearAutoHide() {
    if (_autoHideTimer !== null) {
        clearTimeout(_autoHideTimer);
        _autoHideTimer = null;
    }
}

/** Reset the underlying page form; its native 'reset' event regenerates the idempotency key. */
function resetApplicationForm() {
    $('.application-form').each(function () {
        this.reset();
    });
}

/**
 * Show the loading screen and fill the 10 life-indicator progress bar in step with the
 * actual asset preload (object sprites first, then the splash debris set). Each indicator
 * represents 10 % of the assets; once everything is cached, reveal the confirmation screen.
 */
function runLoading() {
    show('loading');

    const startedAt = performance.now();
    const $bar = $modal().find('.loading-stars');
    const $stars = $bar.find('.loading-star').removeClass('is-full');
    const total = $stars.length || 1;
    $bar.attr('aria-valuenow', 0);

    const paint = (progress) => {
        const filled = Math.round(progress * total);
        $stars.each(function (i) {
            $(this).toggleClass('is-full', i < filled);
        });
        $bar.attr('aria-valuenow', filled);
    };

    // Kick off the asset preload, but drive the bar by elapsed time so it fills smoothly
    // and linearly over MIN_LOADING_MS rather than snapping to full on a cache hit.
    let preloadDone = false;
    preloadNinjaCutAssets().then(() => { preloadDone = true; });

    const tick = () => {
        const timeFrac = Math.min(1, (performance.now() - startedAt) / MIN_LOADING_MS);
        // Advance linearly with time; if assets aren't ready yet, hold just short of full.
        paint(preloadDone ? timeFrac : Math.min(timeFrac, 0.9));

        if (timeFrac >= 1 && preloadDone) {
            paint(1);
            if (!LOADING_AUTO_PROCEED) {
                return; // TEMP: stay on the loading screen for styling
            }
            if (_data) {
                setRegisteredData(_data, MODAL_SELECTOR);
            }
            show('confirmation');
            return;
        }
        requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
}

function gameContainer() {
    return $modal().find('#game-canvas-container').get(0);
}

/** Real game: the score is registered server-side by /ninja-cut/result; here we reveal the result. */
function startGame() {
    // The game is starting → the 2 h start window no longer applies; stop the auto-hide timer
    // so the modal can't vanish mid-game.
    clearAutoHide();
    show('game');
    startNinjaCut(gameContainer(), {
        demo: false,
        uuid: _uuid,
        onDone: function (score, reason) {
            _score = score;
            _reason = reason;
            renderResult();
            show('result');
        },
        onError: function (error) {
            // /start can reject with a code we map to a dedicated screen; anything else falls
            // back to a neutral score-0 result.
            const code = error && error.data && error.data.error;
            if (code === 'outside_hours') {
                return showOutsideHours(error.data);
            }
            if (code === 'start_window_expired') {
                return show('start-expired');
            }
            _score = 0;
            renderResult();
            show('result');
        },
    });
}

/** Fill the "outside play hours" screen with the configured window and reveal it. */
function showOutsideHours(data) {
    const start = data && data.startHour != null ? data.startHour : null;
    const end = data && data.endHour != null ? data.endHour : null;
    if (start != null && end != null) {
        $modal().find('.outside-hours-range').text(`${start}:00–${end}:00`);
    }
    show('outside-hours');
}

/** Trial game (up to three attempts): score is discarded; finishing advances to the trial-result screen. */
function startDemo() {
    if (_demoAttempts >= MAX_DEMO_ATTEMPTS) {
        return;
    }
    show('game');
    startNinjaCut(gameContainer(), {
        demo: true,
        onDone: function () {
            _demoAttempts++;
            renderDemoResult();
            show('demo-result');
        },
    });
}

/** Update the trial-result screen for the just-finished attempt (life HUD + replay button). */
function renderDemoResult() {
    const $m = $modal();
    const attempt = Math.max(_demoAttempts, 1);
    $m.find('.demo-result-title').text(`Próba ${attempt} zakończona`);

    const $lives = $m.find('.demo-hud__lives');
    const full = $lives.data('life-full');
    const empty = $lives.data('life-empty');

    // Fill one life indicator per used attempt; leave the rest empty.
    $lives.find('.demo-life').each(function (i) {
        $(this).attr('src', i < _demoAttempts ? full : empty);
    });

    // Offer another attempt only while the user has trials left.
    $m.find('.demo-again').toggleClass('d-none', _demoAttempts >= MAX_DEMO_ATTEMPTS);
}

function renderResult() {
    $modal().find('.result-score').text(_score ?? 0);
    // Prepend the "czas minął" note only when the game ended on the timer, not on lost lives.
    $modal().find('.result-timeout-note').toggleClass('d-none', _reason !== 'timeup');
}

/** Resign from the contest question: run the DECLINE transition and show the thank-you screen. */
function resign(button) {
    const $button = $(button).prop('disabled', true);

    $.ajax({
        type: 'POST',
        url: endpoint('answer'),
        data: { decline: 1, _token: csrf() },
    }).done(function () {
        show('thanks');
    }).fail(function () {
        alert('Nie udało się zapisać rezygnacji. Spróbuj ponownie.');
    }).always(function () {
        $button.prop('disabled', false);
    });
}

function submitAnswer(decline) {
    const $m = $modal();
    const $textarea = $m.find('.quest-answer');
    const $error = $m.find('.quest-answer-error');
    const $buttons = $m.find('[data-modal-action="answer"], [data-modal-action="decline"]');
    const answer = ($textarea.val() || '').trim();

    $error.text('').hide();
    $textarea.removeClass('is-invalid');

    if (!decline && answer === '') {
        $textarea.addClass('is-invalid');
        $error.text('Wpisz odpowiedź').show();
        return;
    }

    $buttons.prop('disabled', true);

    $.ajax({
        type: 'POST',
        url: endpoint('answer'),
        data: { answer: answer, decline: decline ? 1 : 0, _token: csrf() },
    }).done(function () {
        show('thanks');
    }).fail(function (xhr) {
        const message = xhr.responseJSON?.formErrors?.answer?.[0]
            || xhr.responseJSON?.errors?.[0]
            || 'Nie udało się zapisać odpowiedzi. Spróbuj ponownie.';
        $textarea.addClass('is-invalid');
        $error.text(message).show();
    }).always(function () {
        $buttons.prop('disabled', false);
    });
}

// Stop the game and cancel the auto-hide timer whenever the modal is closed.
$(document).on('hidden.bs.modal', MODAL_SELECTOR, function () {
    clearAutoHide();
    teardownNinjaCut();
});

// Orientation guard: on short landscape (mobile) screens the modal shows a "rotate to
// portrait" cover (CSS-only, see s-game-modal.scss — must match this media query). While
// that cover is up over a running game, pause the engine and resume on rotate back.
const ROTATE_MQ = window.matchMedia('(orientation: landscape) and (max-height: 600px)');

function handleRotateGuard(event) {
    if (!$modal().hasClass('show')) {
        return;
    }
    if (event.matches) {
        pauseNinjaCut();
    } else {
        resumeNinjaCut();
    }
}

if (ROTATE_MQ.addEventListener) {
    ROTATE_MQ.addEventListener('change', handleRotateGuard);
} else if (ROTATE_MQ.addListener) {
    ROTATE_MQ.addListener(handleRotateGuard); // Safari < 14
}

// DEV ONLY: jump to the chosen screen (re-runs loading / renders the result score).
$(document).on('change', `${MODAL_SELECTOR} .modal-dev-screen`, function () {
    const screen = $(this).val();
    teardownNinjaCut();
    if (screen === 'loading') {
        return runLoading();
    }
    if (screen === 'result') {
        renderResult();
    }
    if (screen === 'demo-result') {
        renderDemoResult();
    }
    show(screen);
});

// DEV ONLY: open the modal with a stub payload + hardcoded uuid, skipping the form submit.
$(document).on('click', '.application-modal-dev', function () {
    startApplicationModal({
        status: 'OK',
        uuid: $(this).data('dev-uuid'),
        firstname: 'Jan',
        lastname: 'Kowalski',
        email: 'jan.kowalski@example.com',
        phone: '500600700',
        billType: 'Paragon',
        billNr: '0001',
        billDate: '01.01.2026',
        billNip: null,
        billPos: '01',
        prize: null,
        formUrl: null,
    });
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-next]`, function () {
    show($(this).data('modal-next'));
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-action]`, function () {
    switch ($(this).data('modal-action')) {
        case 'start-game':
            return startGame();
        case 'start-demo':
            return startDemo();
        case 'resign':
            return resign(this);
        case 'answer':
            return submitAnswer(false);
        case 'decline':
            return submitAnswer(true);
    }
});
